import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { X } from "@phosphor-icons/react";
import { StatusBadge } from "../StatusBadge";
import { MARKETPLACES, PLATFORM_BY_KEY } from "../../platforms";
import { metaFor, CAT_TEXT } from "../../eventMeta";
import { fmtDateTime } from "../../campaignUtils";

// Read-only side panel for a timeline event: mapped fields + raw payload.
export function EventDetailPanel({ event, onClose }) {
  const { t } = useTranslation();
  if (!event) return <AnimatePresence />;
  const { Icon, cat } = metaFor(event.type);
  const mkt = event._marketplace ? MARKETPLACES.find((m) => m.key === event._marketplace) : null;
  const plat = event._platform ? PLATFORM_BY_KEY[event._platform] : null;

  const Field = ({ label, value }) => value == null || value === "" ? null : (
    <div><div className="promo-overline">{label}</div><div className="mt-1 text-sm font-medium">{value}</div></div>
  );

  return (
    <AnimatePresence>
      {event && (
        <div className="fixed inset-0 z-[60]" data-testid="event-detail-panel">
          <motion.div className="absolute inset-0 bg-background/70 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
          <motion.aside className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col border-l border-border bg-card"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "tween", duration: 0.25 }}>
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <div className="flex items-center gap-2"><Icon size={16} className={CAT_TEXT[cat]} /><span className="promo-overline">{t(`promo.timeline.cat.${cat}`, cat)}</span></div>
              <button onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-accent" data-testid="event-panel-close"><X size={18} /></button>
            </div>

            <div className="flex-1 space-y-5 overflow-y-auto p-5">
              <div className="flex items-center justify-between gap-3">
                <h2 className="break-all font-mono text-sm font-medium">{event.type}</h2>
                {event._status && <StatusBadge status={event._status} label={t(`promo.queue.status.${event._status}`, event._status)} />}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Field label={t("promo.timeline.f.timestamp")} value={fmtDateTime(event.created_at)} />
                <Field label={t("promo.timeline.f.campaign")} value={event._campaign} />
                <Field label={t("promo.timeline.f.product")} value={event._product} />
                <Field label={t("promo.timeline.f.marketplace")} value={mkt?.label || event._marketplace} />
                <Field label={t("promo.timeline.f.platform")} value={plat?.label || event._platform} />
                <Field label={t("promo.timeline.f.actor")} value={event.actor_email} />
              </div>

              <div>
                <div className="promo-overline mb-2">{t("promo.timeline.f.payload")}</div>
                <pre className="overflow-x-auto rounded-lg border border-border bg-accent/20 p-3 font-mono text-[11px] leading-relaxed text-foreground/90" data-testid="event-payload">
{JSON.stringify(event.payload || {}, null, 2)}
                </pre>
              </div>

              {event.correlation_id && (
                <Field label={t("promo.timeline.f.correlation")} value={<span className="font-mono text-xs">{event.correlation_id}</span>} />
              )}
            </div>
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
}
